import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ESTIMATE_CATEGORIES } from "@/lib/constants";
import { fmtRupiah } from "@/lib/format";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, Plus, Trash2 } from "lucide-react";

const emptyItem = () => ({ category: "Jasa Pengerjaan", name: "", qty: 1, unit: "unit", unit_price: 0 });

export default function EstimateForm() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ project_uuid: sp.get("project") || "", date: new Date().toISOString().slice(0, 10), valid_until: "", work_type: "", description: "", target_days: 0, pic_name: "", notes: "", payment_terms: "", items: [emptyItem()], discount: 0, tax_percent: 11 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/projects");
      setProjects(data);
      if (editing) {
        const { data: e } = await api.get(`/estimates/${id}`);
        setForm({ ...e, items: e.items.length ? e.items : [emptyItem()] });
      }
      setLoading(false);
    })();
  }, [id, editing]);

  const setItem = (i, k, v) => setForm((f) => { const items = [...f.items]; items[i] = { ...items[i], [k]: v }; return { ...f, items }; });
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const delItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const subtotal = form.items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_price) || 0), 0);
  const taxable = Math.max(0, subtotal - (Number(form.discount) || 0));
  const taxAmount = taxable * (Number(form.tax_percent) || 0) / 100;
  const grand = taxable + taxAmount;

  const save = async () => {
    if (!form.project_uuid || !form.items.some((it) => it.name)) { toast.error("Pilih proyek dan isi minimal satu item."); return; }
    setSaving(true);
    try {
      const payload = { ...form, target_days: Number(form.target_days) || 0, discount: Number(form.discount) || 0, tax_percent: Number(form.tax_percent) || 0, items: form.items.filter((it) => it.name).map((it) => ({ ...it, qty: Number(it.qty) || 0, unit_price: Number(it.unit_price) || 0 })) };
      const { data } = editing ? await api.put(`/estimates/${id}`, payload) : await api.post("/estimates", payload);
      toast.success(`Penawaran ${data.estimate_no} disimpan.`);
      navigate(`/estimates/${data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4 max-w-4xl pb-20" data-testid="estimate-form-page">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" onClick={() => navigate(-1)} data-testid="back-button"><ArrowLeft size={18} /></Button>
        <h1 className="font-heading text-xl font-bold text-navy">{editing ? "Edit Penawaran" : "Buat Penawaran"}</h1>
      </div>

      <Card className="p-4 space-y-3">
        <h3 className="font-heading font-semibold text-navy text-sm border-b pb-2">Data Penawaran</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1 sm:col-span-2"><Label>Proyek (Project Master) *</Label>
            <Select value={form.project_uuid} onValueChange={(v) => set("project_uuid", v)} disabled={editing}>
              <SelectTrigger data-testid="est-project"><SelectValue placeholder="Pilih proyek" /></SelectTrigger>
              <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.project_id} · {p.plate} · {p.customer_name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1"><Label>Tanggal Penawaran</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="est-date" /></div>
          <div className="space-y-1"><Label>Masa Berlaku</Label><Input type="date" value={form.valid_until || ""} onChange={(e) => set("valid_until", e.target.value)} data-testid="est-valid" /></div>
          <div className="space-y-1"><Label>Jenis Pekerjaan</Label><Input value={form.work_type} onChange={(e) => set("work_type", e.target.value)} data-testid="est-worktype" /></div>
          <div className="space-y-1"><Label>Target Waktu (hari)</Label><Input type="number" value={form.target_days} onChange={(e) => set("target_days", e.target.value)} data-testid="est-target" /></div>
          <div className="space-y-1"><Label>PIC / Estimator</Label><Input value={form.pic_name} onChange={(e) => set("pic_name", e.target.value)} data-testid="est-pic" /></div>
        </div>
        <div className="space-y-1"><Label>Deskripsi Pekerjaan</Label><Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="est-desc" /></div>
      </Card>

      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between"><h3 className="font-heading font-semibold text-navy text-sm">Rincian Biaya</h3>
          <Button size="sm" variant="outline" onClick={addItem} data-testid="est-add-item"><Plus size={14} className="mr-1" /> Item</Button></div>
        <div className="space-y-2">
          {form.items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`est-item-${i}`}>
              <Select value={it.category} onValueChange={(v) => setItem(i, "category", v)}>
                <SelectTrigger className="col-span-6 sm:col-span-2 h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{ESTIMATE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <Input className="col-span-6 sm:col-span-4 h-9" placeholder="Uraian item" value={it.name} onChange={(e) => setItem(i, "name", e.target.value)} data-testid={`est-item-name-${i}`} />
              <Input className="col-span-3 sm:col-span-1 h-9" type="number" placeholder="Qty" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} />
              <Input className="col-span-3 sm:col-span-1 h-9" placeholder="Satuan" value={it.unit} onChange={(e) => setItem(i, "unit", e.target.value)} />
              <Input className="col-span-4 sm:col-span-2 h-9" type="number" placeholder="Harga satuan" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} data-testid={`est-item-price-${i}`} />
              <div className="col-span-1 text-right"><button onClick={() => delItem(i)} className="text-red-500"><Trash2 size={15} /></button></div>
            </div>
          ))}
        </div>
        <div className="grid sm:grid-cols-3 gap-3 border-t pt-3">
          <div className="space-y-1"><Label>Diskon (Rp)</Label><Input type="number" value={form.discount} onChange={(e) => set("discount", e.target.value)} data-testid="est-discount" /></div>
          <div className="space-y-1"><Label>Pajak (%)</Label><Input type="number" value={form.tax_percent} onChange={(e) => set("tax_percent", e.target.value)} data-testid="est-tax" /></div>
          <div className="space-y-1"><Label>Syarat Pembayaran</Label><Input value={form.payment_terms} onChange={(e) => set("payment_terms", e.target.value)} data-testid="est-terms" /></div>
        </div>
        <div className="bg-slate-50 rounded-md p-3 text-sm space-y-1">
          <div className="flex justify-between"><span>Subtotal</span><span>{fmtRupiah(subtotal)}</span></div>
          <div className="flex justify-between"><span>Diskon</span><span>-{fmtRupiah(form.discount)}</span></div>
          <div className="flex justify-between"><span>Pajak</span><span>{fmtRupiah(taxAmount)}</span></div>
          <div className="flex justify-between font-bold text-navy text-base border-t pt-1"><span>Grand Total</span><span data-testid="est-grand">{fmtRupiah(grand)}</span></div>
        </div>
        <Textarea rows={2} placeholder="Catatan & syarat" value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="est-notes" />
      </Card>

      <div className="flex gap-2 sticky bottom-0 bg-background py-3 border-t">
        <Button onClick={save} disabled={saving} data-testid="est-save-btn" className="bg-primary hover:bg-red-700 h-11">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save size={16} className="mr-1.5" />} Simpan Draft</Button>
        <Button variant="ghost" onClick={() => navigate(-1)} className="h-11">Batal</Button>
      </div>
    </div>
  );
}
